import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MousePointerClick, Sparkles, Type, Upload } from 'lucide-react';
import { useI18n } from '@/context/LanguageContext';

const BEFORE_IMG =
  'https://customer-assets-39nsmqrw.emergentagent.net/job_b9f22a8a-5674-4011-9127-0bb90df15546/artifacts/51c5qxck_maxresdefault-2.jpg';
const AFTER_IMG =
  'https://customer-assets-39nsmqrw.emergentagent.net/job_b9f22a8a-5674-4011-9127-0bb90df15546/artifacts/840mvufq_fd.jpg';

const Frame = ({ children, label, tone = 'muted', testId }) => (
  <div
    data-testid={testId}
    className="relative overflow-hidden rounded-[14px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface-2))]"
    style={{ aspectRatio: '16/9' }}
  >
    {children}
    {label && (
      <span
        className={[
          'absolute left-2.5 top-2.5 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide',
          tone === 'brand'
            ? 'bg-[hsl(var(--brand-red))] text-white'
            : 'bg-black/70 text-[hsl(var(--text-2))]',
        ].join(' ')}
      >
        {label}
      </span>
    )}
  </div>
);

export const HowItWorks = () => {
  const { t } = useI18n();

  const edits = [t('how.edit1')];

  const steps = [
    {
      n: '01',
      icon: Upload,
      title: t('how.s1Title'),
      desc: t('how.s1Desc'),
      visual: (
        <Frame label={t('how.beforeLabel')} testId="how-before-frame">
          <img
            src={BEFORE_IMG}
            alt={t('how.beforeAlt')}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </Frame>
      ),
    },
    {
      n: '02',
      icon: Type,
      title: t('how.s2Title'),
      desc: t('how.s2Desc'),
      visual: (
        <Frame testId="how-edits-frame">
          <div className="flex h-full w-full flex-col justify-center gap-2 p-4">
            <span className="text-[10px] font-bold tracking-wide text-[hsl(var(--text-muted))]">
              {t('how.editsLabel')}
            </span>
            {edits.map((e) => (
              <span
                key={e}
                className="flex items-center gap-2 rounded-[8px] border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] px-2.5 py-1.5 text-[11px] leading-snug text-[hsl(var(--text-2))]"
              >
                <MousePointerClick className="h-3 w-3 shrink-0 text-[hsl(var(--brand-red))]" />
                {e}
              </span>
            ))}
          </div>
        </Frame>
      ),
    },
    {
      n: '03',
      icon: Sparkles,
      title: t('how.s3Title'),
      desc: t('how.s3Desc'),
      visual: (
        <Frame label={t('how.afterLabel')} tone="brand" testId="how-after-frame">
          <img
            src={AFTER_IMG}
            alt={t('how.afterAlt')}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </Frame>
      ),
    },
  ];

  return (
    <section
      id="nasil-calisir"
      data-testid="how-it-works-section"
      className="relative overflow-hidden border-t border-[hsl(var(--border-soft))] bg-[hsl(var(--bg-2))]"
    >
      <div className="mx-auto max-w-[1100px] px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-[hsl(var(--brand-red))]">
            {t('how.eyebrow')}
          </span>
          <h2
            data-testid="how-it-works-title"
            className="mt-3 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl"
          >
            {t('how.title')}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[hsl(var(--text-2))]">
            {t('how.subtitle')}
          </p>
        </div>

        <div className="mt-12 grid max-w-[880px] gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.35, delay: i * 0.1 }}
              className="relative"
            >
              <div
                data-testid={`how-step-${i + 1}`}
                className="panel h-full p-4 transition-colors duration-150 hover:border-[hsl(var(--border-strong))]"
              >
                {s.visual}

                <div className="mt-4 flex items-start gap-3">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-[hsl(var(--surface-2))] text-[hsl(var(--brand-red))]">
                    <s.icon className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-alt text-[11px] font-bold text-[hsl(var(--brand-red))]">
                        {s.n}
                      </span>
                      <h3 className="font-display text-base font-semibold">{s.title}</h3>
                    </div>
                    <p className="mt-1.5 text-sm leading-relaxed text-[hsl(var(--text-muted))]">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </div>

              {i < steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute -right-4 top-1/2 hidden h-8 w-8 -translate-y-1/2 place-items-center rounded-full border border-[hsl(var(--border-soft))] bg-[hsl(var(--surface))] text-[hsl(var(--brand-red))] lg:grid"
                >
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-xs text-[hsl(var(--text-muted))]">{t('how.note')}</p>
      </div>
    </section>
  );
};
